import React, { useState, useEffect, useCallback } from 'react';
import { transactionsApi, categoriesApi, accountsApi } from '../services/api';
import { fmt } from '../utils/format';
import Modal from '../components/Modal';

const NON_TX_ACCOUNTS = new Set(['Cash', 'Other Liquid Assets']);

export default function TransactionsTab({ month, onRefresh }) {
  const [txs, setTxs]           = useState([]);
  const [cats, setCats]         = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [acctFilter, setAcctFilter] = useState('All');
  const [search, setSearch]     = useState('');
  const [editTx, setEditTx]     = useState(null);
  const [transferError, setTransferError] = useState(null);

  const today = new Date().toISOString().split('T')[0];
  const [form, setForm] = useState({
    date: today, description: '', amount: '', type: 'Debit',
    categoryName: '', accountName: 'Checking', toAccountName: '', interest: '',
  });
  const isTransfer = form.type === 'Transfer';
  const destAccount = accounts.find(a => a.name === form.toAccountName);
  // Only a card/loan payment can carry interest — a move between your own cash
  // accounts is the same money on both sides.
  const payingCard = isTransfer && !!destAccount?.isCreditCard;

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      transactionsApi.getAll(month.id),
      categoriesApi.getAll(month.id),
      accountsApi.getAll(month.id),
    ]).then(([tR, cR, aR]) => {
      setTxs(tR.data); setCats(cR.data); setAccounts(aR.data); setLoading(false);
    });
  }, [month.id]);

  useEffect(() => { load(); }, [load]);

  const txAccounts = accounts.filter(a => a.accountType === 'Budget' && !NON_TX_ACCOUNTS.has(a.name));

  // Keep accountName in sync when account list changes
  useEffect(() => {
    if (txAccounts.length > 0 && !txAccounts.find(a => a.name === form.accountName)) {
      setForm(f => ({ ...f, accountName: txAccounts[0].name }));
    }
    // Also set a default toAccountName if empty
    if (txAccounts.length > 1 && !form.toAccountName) {
      setForm(f => ({ ...f, toAccountName: txAccounts[1]?.name || txAccounts[0].name }));
    }
  }, [accounts.length]); // eslint-disable-line

  // The To dropdown hides the source account, but hiding an <option> doesn't
  // change the value behind it: picking a source equal to the current
  // destination left the select showing one account while the form still held
  // the other. Re-point it whenever it goes stale so what's displayed is what
  // gets submitted.
  useEffect(() => {
    if (!isTransfer) return;
    const choices = txAccounts.filter(a => a.name !== form.accountName);
    if (choices.length === 0) return;
    if (!choices.some(a => a.name === form.toAccountName)) {
      setForm(f => ({ ...f, toAccountName: choices[0].name }));
    }
  }, [isTransfer, form.accountName, form.toAccountName, accounts.length]); // eslint-disable-line

  const handleAdd = async () => {
    // Amount is always required
    if (!form.amount) return;
    // Description required only for non-transfers
    if (!isTransfer && !form.description.trim()) return;
    // For transfers, need a destination account — and a different one. Moving
    // money from an account to itself writes two rows that cancel out, which
    // reads as a completed payment that never happened.
    if (isTransfer && !form.toAccountName) return;
    if (isTransfer && form.toAccountName === form.accountName) {
      setTransferError('Pick a different account to transfer to.');
      return;
    }
    if (payingCard && parseFloat(form.interest || 0) > parseFloat(form.amount)) {
      setTransferError("Interest can't be more than the payment.");
      return;
    }
    setTransferError(null);

    const amt  = parseFloat(form.amount);
    const date = form.date || today;

    if (isTransfer) {
      // Paying a credit card spends the envelope that exists to fund it. The
      // money-out leg carries the card's category, so Available drops the way it
      // would for any other spending — without this a card payment left the
      // envelope showing the full amount as still available.
      const cardCategory = payingCard ? form.toAccountName : '';
      const interest = payingCard ? (parseFloat(form.interest) || 0) : 0;

      const legs = [
        {
          date,
          description: `Transfer to ${form.toAccountName}`,
          amount: amt, type: 'Debit', categoryName: cardCategory,
          accountName: form.accountName,
        },
        {
          date,
          description: `Transfer from ${form.accountName}`,
          amount: amt, type: 'Deposit', categoryName: '',
          accountName: form.toAccountName,
        },
      ];

      // Interest isn't principal: the whole payment leaves your cash, but only
      // part of it reduces what you owe. Charging the interest back onto the
      // card leaves the debt down by principal alone, and the card's ledger
      // reads like a statement — charge, interest, payment — instead of the
      // payment silently overshooting the balance into positive territory.
      // Uncategorised on purpose: the budget already spent it via the leg above.
      if (interest > 0) {
        legs.push({
          date,
          description: `Interest & fees — ${form.toAccountName}`,
          amount: interest, type: 'Debit', categoryName: '',
          accountName: form.toAccountName,
        });
      }

      // Sequential, not Promise.all: the payment and its interest charge both
      // land on the destination account, and the server applies each one with an
      // unlocked read-modify-write of the stored balance. Fired concurrently
      // they race and one silently overwrites the other.
      for (const leg of legs) {
        await transactionsApi.add(month.id, leg);
      }
      // Reset form — go back to Debit so next entry isn't silently a transfer
      setForm(f => ({ ...f, amount: '', type: 'Debit', description: '', interest: '' }));
    } else {
      await transactionsApi.add(month.id, {
        date, description: form.description.trim(), amount: amt,
        type: form.type, categoryName: form.categoryName,
        accountName: form.accountName,
      });
      setForm(f => ({ ...f, description: '', amount: '', categoryName: '' }));
    }
    load(); onRefresh();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this transaction?')) return;
    await transactionsApi.delete(id); load(); onRefresh();
  };

  const handleEdit = async () => {
    if (!editTx) return;
    await transactionsApi.update(editTx.id, {
      date: editTx.date, description: editTx.description,
      amount: parseFloat(editTx.amount),
      type: editTx.type, categoryName: editTx.categoryName,
      accountName: editTx.accountName,
    });
    setEditTx(null); load(); onRefresh();
  };

  const isTransferTx = (tx) =>
    (tx.description || '').startsWith('Transfer to ') ||
    (tx.description || '').startsWith('Transfer from ');

  const filtered = txs
    .filter(t => acctFilter === 'All' || t.accountName === acctFilter)
    .filter(t => !search ||
      (t.description || '').toLowerCase().includes(search.toLowerCase()) ||
      (t.categoryName || '').toLowerCase().includes(search.toLowerCase()));

  const withBal = acctFilter !== 'All' ? (() => {
    const ordered = [...filtered].reverse();
    let bal = 0;
    return ordered.map(tx => {
      if (tx.type === 'Deposit') bal += tx.amount; else bal -= tx.amount;
      return { ...tx, runningBal: bal };
    }).reverse();
  })() : filtered;

  const assignDeps  = txs.filter(t => t.categoryName === 'Up for Grabs' && t.type === 'Deposit').reduce((s, t) => s + t.amount, 0);
  const totalDebits = txs.filter(t => t.type === 'Debit' && !isTransferTx(t)).reduce((s, t) => s + t.amount, 0);

  const allAcctNames = ['All', ...txAccounts.map(a => a.name)];

  return (
    <>
      <div className="metric-grid">
        <div className="metric-box"><div className="label">Up for Grabs</div><div className="value pos">{fmt(assignDeps)}</div></div>
        <div className="metric-box"><div className="label">Spent</div><div className="value neg">{fmt(-totalDebits)}</div></div>
        <div className="metric-box"><div className="label">Count</div><div className="value">{txs.length}</div></div>
      </div>

      {/* Add form */}
      <div className="card">
        <div className="sec-label">New entry</div>
        <div className="form-row">
          <div className="form-group">
            <label>Type</label>
            <select className="field-input" style={{ width: 110 }} value={form.type}
              onChange={e => setForm(f => ({ ...f, type: e.target.value }))}>
              <option>Debit</option>
              <option>Deposit</option>
              <option>Transfer</option>
            </select>
          </div>
          <div className="form-group">
            <label>Date</label>
            <input className="field-input" type="date" style={{ width: 145 }} value={form.date}
              onChange={e => setForm(f => ({ ...f, date: e.target.value }))} />
          </div>
          {!isTransfer && (
            <div className="form-group">
              <label>Description</label>
              <input className="field-input" style={{ width: 190 }} placeholder="Walmart, Paycheck…" value={form.description}
                onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                onKeyDown={e => e.key === 'Enter' && handleAdd()} />
            </div>
          )}
          <div className="form-group">
            <label>Amount</label>
            <input className="field-input" type="number" step="0.01" style={{ width: 110 }} placeholder="0.00" value={form.amount}
              onChange={e => setForm(f => ({ ...f, amount: e.target.value }))}
              onKeyDown={e => e.key === 'Enter' && handleAdd()} />
          </div>
          {!isTransfer && (
            <div className="form-group">
              <label>Category</label>
              <select className="field-input" style={{ width: 175 }} value={form.categoryName}
                onChange={e => setForm(f => ({ ...f, categoryName: e.target.value }))}>
                <option value="">— none —</option>
                {cats.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
                <option value="Up for Grabs">Up for Grabs</option>
              </select>
            </div>
          )}
          <div className="form-group">
            <label>{isTransfer ? 'From' : 'Account'}</label>
            <select className="field-input" style={{ width: 145 }} value={form.accountName}
              onChange={e => setForm(f => ({ ...f, accountName: e.target.value }))}>
              {txAccounts.map(a => <option key={a.id} value={a.name}>{a.name}</option>)}
            </select>
          </div>
          {isTransfer && (
            <div className="form-group">
              <label>To</label>
              <select className="field-input" style={{ width: 145 }} value={form.toAccountName}
                onChange={e => setForm(f => ({ ...f, toAccountName: e.target.value }))}>
                {txAccounts.filter(a => a.name !== form.accountName).map(a =>
                  <option key={a.id} value={a.name}>{a.name}</option>
                )}
              </select>
            </div>
          )}
          {payingCard && (
            <div className="form-group">
              <label>Interest &amp; fees</label>
              <input className="field-input" type="number" step="0.01" min="0" style={{ width: 110 }}
                placeholder="0.00" value={form.interest}
                title="The part of this payment that isn't principal. It still leaves your account, but it doesn't reduce what you owe."
                onChange={e => setForm(f => ({ ...f, interest: e.target.value }))} />
            </div>
          )}
          {payingCard && (() => {
            const total = parseFloat(form.amount) || 0;
            const int   = parseFloat(form.interest) || 0;
            const principal = Math.round((total - int) * 100) / 100;
            return (
              <div style={{ alignSelf: 'flex-end', fontSize: 11, color: 'var(--text3)', maxWidth: 230, lineHeight: 1.45, paddingBottom: 6 }}>
                Spends the <strong>{form.toAccountName}</strong> envelope.
                {int > 0 && total > 0 && (
                  <> Owed drops by <strong>{fmt(principal)}</strong>, not {fmt(total)}.</>
                )}
              </div>
            );
          })()}
          <button className="btn btn-primary" style={{ alignSelf: 'flex-end' }} onClick={handleAdd}>
            {isTransfer ? 'Transfer' : 'Add'}
          </button>
        </div>
        {transferError && (
          <div style={{ fontSize: 12, color: 'var(--red)', marginTop: 8 }}>{transferError}</div>
        )}
      </div>

      {/* Filter bar */}
      <div className="card" style={{ padding: '12px 20px' }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase' }}>Account</span>
          {allAcctNames.map(a => (
            <button key={a} className={`btn btn-sm${acctFilter === a ? ' btn-primary' : ''}`}
              onClick={() => setAcctFilter(a)}>{a}</button>
          ))}
          <input className="field-input" placeholder="Search…" style={{ marginLeft: 'auto', width: 180 }}
            value={search} onChange={e => setSearch(e.target.value)} />
        </div>
      </div>

      {/* Ledger */}
      <div className="card">
        {loading ? <div className="spinner" /> : (
          <div style={{ overflowX: 'auto' }}>
            <table className="tbl">
              <thead>
                <tr>
                  <th>Date</th><th>Description</th><th>Account</th><th>Category</th>
                  <th className="r">Amount</th><th>Type</th>
                  {acctFilter !== 'All' && <th className="r">Balance</th>}
                  <th style={{ width: 70 }}></th>
                </tr>
              </thead>
              <tbody>
                {withBal.length === 0 && (
                  <tr><td colSpan={8} style={{ textAlign: 'center', color: 'var(--text3)', padding: 32 }}>No transactions.</td></tr>
                )}
                {withBal.map(tx => {
                  const transfer = isTransferTx(tx);
                  return (
                    <tr key={tx.id}>
                      <td style={{ color: 'var(--text2)', whiteSpace: 'nowrap', fontSize: 12 }}>{tx.date}</td>
                      <td>{tx.description}</td>
                      <td style={{ color: 'var(--text2)' }}>{tx.accountName}</td>
                      <td style={{ color: 'var(--text2)' }}>{tx.categoryName || '—'}</td>
                      <td className={`r ${tx.type === 'Debit' ? 'neg' : 'pos'}`}>
                        {tx.type === 'Debit' ? '−' : '+'}{fmt(tx.amount)}
                      </td>
                      <td>
                        {transfer
                          ? <span className="badge badge-transfer">Transfer</span>
                          : <span className={`badge badge-${tx.type.toLowerCase()}`}>{tx.type}</span>
                        }
                      </td>
                      {acctFilter !== 'All' && (
                        <td className={`r ${tx.runningBal < 0 ? 'neg' : ''}`}>{fmt(tx.runningBal || 0)}</td>
                      )}
                      <td style={{ display: 'flex', gap: 4 }}>
                        <button className="btn btn-sm" onClick={() => setEditTx({ ...tx, amount: tx.amount.toFixed(2) })}>✎</button>
                        <button className="btn btn-danger btn-sm" onClick={() => handleDelete(tx.id)}>✕</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {editTx && (
        <Modal title="Edit" onClose={() => setEditTx(null)}>
          <div className="form-group" style={{ marginBottom: 10 }}>
            <label>Date</label>
            <input className="field-input" type="date" style={{ width: '100%' }}
              value={editTx.date} onChange={e => setEditTx(t => ({ ...t, date: e.target.value }))} />
          </div>
          <div className="form-group" style={{ marginBottom: 10 }}>
            <label>Description</label>
            <input className="field-input" style={{ width: '100%' }} value={editTx.description}
              onChange={e => setEditTx(t => ({ ...t, description: e.target.value }))} />
          </div>
          <div className="form-group" style={{ marginBottom: 10 }}>
            <label>Amount</label>
            <input className="field-input" type="number" step="0.01" style={{ width: '100%' }}
              value={editTx.amount} onChange={e => setEditTx(t => ({ ...t, amount: e.target.value }))} />
          </div>
          <div className="form-group" style={{ marginBottom: 10 }}>
            <label>Type</label>
            <select className="field-input" style={{ width: '100%' }} value={editTx.type}
              onChange={e => setEditTx(t => ({ ...t, type: e.target.value }))}>
              <option>Debit</option><option>Deposit</option>
            </select>
          </div>
          <div className="form-group" style={{ marginBottom: 10 }}>
            <label>Category</label>
            <select className="field-input" style={{ width: '100%' }} value={editTx.categoryName || ''}
              onChange={e => setEditTx(t => ({ ...t, categoryName: e.target.value }))}>
              <option value="">— none —</option>
              {cats.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
              <option value="Up for Grabs">Up for Grabs</option>
            </select>
          </div>
          <div className="form-group" style={{ marginBottom: 20 }}>
            <label>Account</label>
            <select className="field-input" style={{ width: '100%' }} value={editTx.accountName}
              onChange={e => setEditTx(t => ({ ...t, accountName: e.target.value }))}>
              {txAccounts.map(a => <option key={a.id} value={a.name}>{a.name}</option>)}
            </select>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-primary" onClick={handleEdit}>Save</button>
            <button className="btn" onClick={() => setEditTx(null)}>Cancel</button>
          </div>
        </Modal>
      )}
    </>
  );
}
